/**
 * ControlLibrary Component
 *
 * Modal/drawer showing suggested controls based on hazard type.
 * Pre-populates from workboat tasks data.
 *
 * @module components/ControlLibrary
 */

import React, { useState, useMemo } from 'react';
import {
  X,
  Search,
  Shield,
  CheckCircle,
  ChevronRight,
  Ban,
  RefreshCw,
  Settings,
  FileText,
  ShieldAlert
} from 'lucide-react';
import { CONTROL_HIERARCHY } from '../config/moduleConfig';

/**
 * Common control measures library
 * Organized by control hierarchy level
 */
const CONTROL_LIBRARY = {
  elimination: [
    { id: 'elim-1', text: 'Avoid working at height by using alternative methods', category: 'Working at Height' },
    { id: 'elim-2', text: 'Eliminate confined space entry by using remote inspection', category: 'Confined Spaces' },
    { id: 'elim-3', text: 'Remove hazardous substances from the process', category: 'Hazardous Substances' },
    { id: 'elim-4', text: 'Eliminate manual handling by using mechanical aids', category: 'Manual Handling' },
    { id: 'elim-5', text: 'Cancel non-essential work during adverse weather', category: 'Weather' }
  ],
  substitution: [
    { id: 'sub-1', text: 'Use water-based paints instead of solvent-based', category: 'Hazardous Substances' },
    { id: 'sub-2', text: 'Replace pneumatic tools with battery-powered alternatives', category: 'Equipment' },
    { id: 'sub-3', text: 'Substitute hazardous chemical with less toxic alternative', category: 'Hazardous Substances' },
    { id: 'sub-4', text: 'Use synthetic ropes instead of wire ropes where possible', category: 'Lifting Operations' },
    { id: 'sub-5', text: 'Replace older equipment with modern safety-designed versions', category: 'Equipment' }
  ],
  engineering: [
    { id: 'eng-1', text: 'Install machine guards and interlocks', category: 'Machinery' },
    { id: 'eng-2', text: 'Provide ventilation/extraction systems', category: 'Air Quality' },
    { id: 'eng-3', text: 'Install handrails and guardrails', category: 'Fall Prevention' },
    { id: 'eng-4', text: 'Fit noise enclosures or barriers', category: 'Noise' },
    { id: 'eng-5', text: 'Install emergency stop buttons', category: 'Machinery' },
    { id: 'eng-6', text: 'Provide adequate lighting', category: 'Visibility' },
    { id: 'eng-7', text: 'Install non-slip deck surfaces', category: 'Slips & Trips' },
    { id: 'eng-8', text: 'Fit safety nets below work area', category: 'Working at Height' },
    { id: 'eng-9', text: 'Install fixed ladder cages', category: 'Fall Prevention' },
    { id: 'eng-10', text: 'Provide gas detection systems', category: 'Confined Spaces' }
  ],
  administrative: [
    { id: 'admin-1', text: 'Develop safe work procedures (SWP)', category: 'Procedures' },
    { id: 'admin-2', text: 'Implement permit-to-work system', category: 'Permits' },
    { id: 'admin-3', text: 'Provide task-specific training', category: 'Training' },
    { id: 'admin-4', text: 'Conduct toolbox talks before work', category: 'Communication' },
    { id: 'admin-5', text: 'Display warning signs and labels', category: 'Signage' },
    { id: 'admin-6', text: 'Implement job rotation to reduce fatigue', category: 'Fatigue' },
    { id: 'admin-7', text: 'Establish exclusion zones during operations', category: 'Access Control' },
    { id: 'admin-8', text: 'Maintain equipment inspection schedules', category: 'Maintenance' },
    { id: 'admin-9', text: 'Conduct regular safety briefings', category: 'Communication' },
    { id: 'admin-10', text: 'Implement buddy system for hazardous work', category: 'Supervision' },
    { id: 'admin-11', text: 'Monitor weather conditions continuously', category: 'Weather' },
    { id: 'admin-12', text: 'Ensure adequate supervision', category: 'Supervision' }
  ],
  ppe: [
    { id: 'ppe-1', text: 'Safety helmet (hard hat)', category: 'Head Protection' },
    { id: 'ppe-2', text: 'Safety glasses/goggles', category: 'Eye Protection' },
    { id: 'ppe-3', text: 'Hearing protection (ear muffs/plugs)', category: 'Hearing Protection' },
    { id: 'ppe-4', text: 'Safety boots (steel toe cap)', category: 'Foot Protection' },
    { id: 'ppe-5', text: 'Hi-visibility clothing', category: 'Visibility' },
    { id: 'ppe-6', text: 'Safety gloves (appropriate type)', category: 'Hand Protection' },
    { id: 'ppe-7', text: 'Fall arrest harness and lanyard', category: 'Fall Protection' },
    { id: 'ppe-8', text: 'Lifejacket/PFD', category: 'Water Safety' },
    { id: 'ppe-9', text: 'Respiratory protection (mask/SCBA)', category: 'Respiratory' },
    { id: 'ppe-10', text: 'Protective coveralls', category: 'Body Protection' },
    { id: 'ppe-11', text: 'Face shield', category: 'Face Protection' },
    { id: 'ppe-12', text: 'Chemical-resistant clothing', category: 'Chemical Protection' }
  ]
};

/**
 * Get icon for control hierarchy level
 */
const getControlIcon = (level) => {
  const icons = {
    elimination: Ban,
    substitution: RefreshCw,
    engineering: Settings,
    administrative: FileText,
    ppe: ShieldAlert
  };
  return icons[level] || Shield;
};

/**
 * ControlLibrary Component
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback to close modal
 * @param {Function} props.onSelect - Callback when control is selected
 * @param {Array} props.selectedControls - Already selected controls
 */
export const ControlLibrary = ({
  isOpen,
  onClose,
  onSelect,
  selectedControls = []
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('all');

  // Filter controls based on search and level
  const filteredControls = useMemo(() => {
    let controls = [];

    Object.entries(CONTROL_LIBRARY).forEach(([level, items]) => {
      if (selectedLevel === 'all' || selectedLevel === level) {
        items.forEach(item => {
          if (
            searchTerm === '' ||
            item.text.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.category.toLowerCase().includes(searchTerm.toLowerCase())
          ) {
            controls.push({ ...item, level });
          }
        });
      }
    });

    return controls;
  }, [searchTerm, selectedLevel]);

  // Group by level for display
  const groupedControls = useMemo(() => {
    const groups = {};
    filteredControls.forEach(control => {
      if (!groups[control.level]) {
        groups[control.level] = [];
      }
      groups[control.level].push(control);
    });
    return groups;
  }, [filteredControls]);

  if (!isOpen) return null;

  const isSelected = (controlId) => {
    return selectedControls.some(c => c.id === controlId);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden">
        {/* Header */}
        <div className="bg-slate-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-6 w-6 text-white" />
            <h2 className="text-xl font-semibold text-white">Control Measures Library</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white hover:bg-slate-500 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search and Filter */}
        <div className="px-6 py-4 border-b border-gray-200 space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search controls..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            />
          </div>

          {/* Level Filter */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedLevel('all')}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                selectedLevel === 'all'
                  ? 'bg-cyan-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            {Object.entries(CONTROL_HIERARCHY).map(([key, control]) => (
              <button
                key={key}
                onClick={() => setSelectedLevel(key)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  selectedLevel === key
                    ? 'bg-cyan-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {control.label}
              </button>
            ))}
          </div>
        </div>

        {/* Control List */}
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(85vh - 240px)' }}>
          {Object.keys(groupedControls).length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <Shield className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No controls found matching your search</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {Object.entries(groupedControls).map(([level, controls]) => {
                const Icon = getControlIcon(level);
                const hierarchyInfo = CONTROL_HIERARCHY[level];

                return (
                  <div key={level}>
                    {/* Level Header */}
                    <div className="px-6 py-3 bg-gray-50 flex items-center gap-3">
                      <Icon className={`h-5 w-5 ${
                        level === 'elimination' ? 'text-green-600' :
                        level === 'substitution' ? 'text-blue-600' :
                        level === 'engineering' ? 'text-cyan-600' :
                        level === 'administrative' ? 'text-yellow-600' :
                        'text-orange-600'
                      }`} />
                      <div>
                        <p className="font-medium text-gray-900">
                          {hierarchyInfo?.label}
                        </p>
                        <p className="text-xs text-gray-500">
                          {hierarchyInfo?.effectiveness}
                        </p>
                      </div>
                    </div>

                    {/* Controls in this level */}
                    {controls.map(control => (
                      <button
                        key={control.id}
                        onClick={() => onSelect(control)}
                        disabled={isSelected(control.id)}
                        className={`
                          w-full px-6 py-3 flex items-center justify-between text-left
                          transition-colors
                          ${isSelected(control.id)
                            ? 'bg-green-50 cursor-default'
                            : 'hover:bg-gray-50 cursor-pointer'
                          }
                        `}
                      >
                        <div className="flex-1">
                          <p className={`text-sm ${
                            isSelected(control.id) ? 'text-green-800' : 'text-gray-900'
                          }`}>
                            {control.text}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {control.category}
                          </p>
                        </div>
                        {isSelected(control.id) ? (
                          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 ml-3" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 ml-3" />
                        )}
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {selectedControls.length} control(s) selected
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Control Hierarchy Guide Component
 * Displays the hierarchy of controls for reference
 */
export const ControlHierarchyGuide = ({ compact = false }) => {
  return (
    <div className={`rounded-lg border border-gray-200 bg-white ${compact ? 'p-3' : 'p-4'}`}>
      <h4 className={`font-medium text-gray-900 ${compact ? 'mb-2 text-sm' : 'mb-3'}`}>
        Hierarchy of Controls (ISO 45001)
      </h4>
      <div className={`space-y-${compact ? '1' : '2'}`}>
        {Object.entries(CONTROL_HIERARCHY).map(([key, control]) => {
          const Icon = getControlIcon(key);
          const colorClasses = {
            elimination: 'bg-green-100 text-green-600',
            substitution: 'bg-blue-100 text-blue-600',
            engineering: 'bg-cyan-100 text-cyan-600',
            administrative: 'bg-yellow-100 text-yellow-600',
            ppe: 'bg-orange-100 text-orange-600'
          };

          return (
            <div key={key} className="flex items-start gap-2">
              <div className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} rounded-full flex items-center justify-center ${colorClasses[key]}`}>
                {compact ? (
                  <span className="text-xs font-bold">{control.level}</span>
                ) : (
                  <Icon className="h-4 w-4" />
                )}
              </div>
              <div className="flex-1">
                <p className={`font-medium ${compact ? 'text-xs' : 'text-sm'} text-gray-900`}>
                  {control.label}
                </p>
                {!compact && (
                  <p className="text-xs text-gray-600">{control.description}</p>
                )}
              </div>
              {!compact && (
                <span className="text-xs text-gray-500">{control.effectiveness}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ControlLibrary;
